import { surveyForm, withForm } from "../hooks/surveyResponseFormHooks";
import { useTranslation } from "react-i18next";
import { useSurveyLayout } from "../useSurveyLayout";
import { faChevronLeft, faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { answerValidator } from "../validators/questions-validator";
import { Button } from "frontend-components";

export const NextButton = withForm({
    ...surveyForm,
    render: function Render({ form }) {
        const { t } = useTranslation();
        const { handleNext, currentQuestionIndex, questions } = useSurveyLayout();
        return (
            <form.Subscribe selector={(state) => state.values}>
                {(values) => {
                    const currentQuestion = questions?.[currentQuestionIndex];
                    const currentAnswer = values.answers?.[currentQuestionIndex];
                    const canContinue =
                        !!currentQuestion &&
                        !!currentAnswer &&
                        answerValidator(currentQuestion).safeParse(currentAnswer).success;

                    return (
                        <Button
                            type="button"
                            variant="fill"
                            onClick={() => {
                                handleNext?.();
                            }}
                            disabled={!canContinue}
                        >
                            {t("common.next")}
                            <FontAwesomeIcon icon={faChevronRight} />
                        </Button>
                    );
                }}
            </form.Subscribe>
        );
    },
});

export const SubmitButton = withForm({
    ...surveyForm,
    render: function Render({ form }) {
        const { t } = useTranslation();
        const { currentQuestionIndex, questions } = useSurveyLayout();
        return (
            <form.Subscribe
                selector={(state) => ({
                    values: state.values,
                    isSubmitting: state.isSubmitting,
                })}
            >
                {({ values, isSubmitting }) => {
                    const currentQuestion = questions?.[currentQuestionIndex];
                    const currentAnswer = values.answers?.[currentQuestionIndex];
                    const canSubmit =
                        !!currentQuestion &&
                        !!currentAnswer &&
                        answerValidator(currentQuestion).safeParse(currentAnswer).success;

                    return (
                        <Button type="submit" variant="fill" disabled={!canSubmit || isSubmitting}>
                            {t("common.submit")}
                        </Button>
                    );
                }}
            </form.Subscribe>
        );
    },
});

export const PreviousButton = withForm({
    ...surveyForm,
    render: function Render() {
        const { t } = useTranslation();
        const { handlePrevious, currentQuestionIndex } = useSurveyLayout();

        return (
            <Button
                type="button"
                onClick={handlePrevious}
                variant="fill"
                disabled={currentQuestionIndex === 0}
            >
                <FontAwesomeIcon icon={faChevronLeft} />
                {t("common.previous")}
            </Button>
        );
    },
});

export const SkipQuestion = withForm({
    ...surveyForm,
    render: function Render({ form }) {
        const { t } = useTranslation();
        const { handleNext, currentQuestionIndex } = useSurveyLayout();

        return (
            <Button
                type="button"
                onClick={() => {
                    form.setFieldValue(`answers[${currentQuestionIndex}].answerValue`, undefined);
                    handleNext?.();
                }}
                variant="ghost"
                className="p-0 hover:underline"
            >
                {t("answers.skipQuestion")}
            </Button>
        );
    },
});

export const SkipQuestionAndSubmitButton = withForm({
    ...surveyForm,
    render: function Render({ form }) {
        const { t } = useTranslation();
        const { currentQuestionIndex } = useSurveyLayout();
        return (
            <form.Subscribe selector={(state) => [state.fieldMeta]}>
                {([fieldMeta]) => {
                    const meta = fieldMeta[`answers[${currentQuestionIndex}].answerValue`];
                    if (!meta) {
                        return null;
                    }
                    return (
                        <Button
                            type="submit"
                            variant="ghost"
                            className="p-0 hover:underline"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                form.setFieldValue(
                                    `answers[${currentQuestionIndex}].answerValue`,
                                    undefined,
                                );
                                form.handleSubmit();
                            }}
                            disabled={form.state.isSubmitting}
                        >
                            {t("answers.skipAndSubmit")}
                        </Button>
                    );
                }}
            </form.Subscribe>
        );
    },
});
